import { useCallback, useEffect, useState } from 'react';
import { AttachmentPicker, type Attachment } from './AttachmentPicker';

const API = import.meta.env.VITE_API_URL || '/api';

const CATEGORY_LABELS: Record<string, string> = {
  order: '📄 Orden',
  send: '📨 Envío',
  reception: '📥 Recepción',
  invoice: '🧾 Factura',
  payment: '💳 Pago',
};

const CATEGORY_ORDER = ['order', 'send', 'reception', 'invoice', 'payment'];

interface Props {
  poId: string;
  onError?: (msg: string) => void;
  /** Notifies the parent of the current attachment count after every reload
   *  (upload/delete included), so tabs can show a live badge. */
  onCountChange?: (count: number) => void;
}

/** Galería de adjuntos de una OC agrupada por paso + subir/borrar. */
export function AttachmentsPanel({ poId, onError, onCountChange }: Props) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [category, setCategory] = useState('reception');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const res = await fetch(`${API}/purchase-orders/${poId}/attachments`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
        'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
      },
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) {
      setAttachments(d.data || []);
      onCountChange?.(d.data?.length || 0);
    } else onError?.(d.error || 'Error al cargar adjuntos');
  }, [poId, onError, onCountChange]);

  useEffect(() => { reload(); }, [reload]);

  const grouped = CATEGORY_ORDER
    .map((c) => ({ category: c, items: attachments.filter((a) => a.category === c) }))
    .filter((g) => g.items.length > 0);
  const orphans = attachments.filter((a) => !CATEGORY_ORDER.includes(a.category));
  const isEmpty = attachments.length === 0;

  async function remove(att: Attachment) {
    if (!window.confirm(`¿Eliminar "${att.fileName}"?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/attachments/${att.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
        },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Error al eliminar');
      }
      await reload();
    } catch (err: any) {
      onError?.(err.message || 'Error al eliminar');
    } finally {
      setBusy(false);
    }
  }

  function renderItem(att: Attachment) {
    const url = `${API}/purchase-orders/${poId}/attachments/${att.id}`;
    const isImage = att.fileType.startsWith('image/');
    const sizeKb = att.fileSize ? Math.max(1, Math.round(att.fileSize / 1024)) : null;
    return (
      <div key={att.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        {isImage ? (
          <a href={url} target="_blank" rel="noreferrer" className="shrink-0">
            <img src={url} alt={att.fileName} className="h-14 w-14 object-cover rounded border border-gray-200 dark:border-gray-600" />
          </a>
        ) : (
          <a href={url} target="_blank" rel="noreferrer" className="shrink-0 flex items-center justify-center h-14 w-14 bg-red-100 dark:bg-red-900/40 text-2xl rounded border border-gray-200 dark:border-gray-600">
            📄
          </a>
        )}
        <div className="flex-1 min-w-0">
          <a href={url} target="_blank" rel="noreferrer" className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate hover:underline" title={att.fileName}>
            {att.fileName}
          </a>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {sizeKb ? `${sizeKb} KB · ` : ''}
            {new Date(att.createdAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <button
          onClick={() => remove(att)}
          disabled={busy}
          title="Eliminar adjunto"
          className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/40 rounded p-2 text-sm min-h-[44px] min-w-[44px]"
        >
          🗑
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isEmpty && (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div key={g.category}>
              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                {CATEGORY_LABELS[g.category] || g.category}
              </h4>
              <div className="space-y-2">{g.items.map(renderItem)}</div>
            </div>
          ))}
          {orphans.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Otros</h4>
              <div className="space-y-2">{orphans.map(renderItem)}</div>
            </div>
          )}
        </div>
      )}
      {isEmpty && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          Sin adjuntos todavía.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Agregar a</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 min-h-[44px]"
        >
          {CATEGORY_ORDER.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <AttachmentPicker poId={poId} category={category} onUploaded={() => reload()} onError={onError} label="Subir archivo" />
      </div>
    </div>
  );
}